import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

// `fonts.css` first, and it is the only one of the four that declares a face. `tokens.css` sets
// `font-family: var(--lm-font-ui)` on `.lm-shell`, and the token holds a whole fallback stack, so
// without this import every screen renders in the stack's second name and Urdu renders in a Latin
// serif that is not text at all. The faces are subset by `unicode-range`, so an English session
// fetches about 49 KB of the 474 KB in `packages/tokens/fonts/` and never touches the 239 KB
// Nastaliq subset. `test/fonts-wired.test.ts` is what stops this line being deleted silently.
import '@lensmira/tokens/fonts.css'
import '@lensmira/tokens/tokens.css'
import '@lensmira/ui/ui.css'
import './app.css'

import { App } from './App.js'
import { PortalApp } from './portal/PortalApp.js'

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

/**
 * `?specimen` opens `DESIGN-TOKENS.md` §17's matrix instead of the counter, in development only.
 *
 * Two things keep it out of a deployed build and both are needed. `import.meta.env.DEV` is replaced
 * by the literal `false` at build time, so the branch is dead code the minifier removes; and the
 * import is dynamic, so Rollup never pulls the module or its fixtures into the bundle in the first
 * place. A static `import { Specimen }` would ship the whole specimen page and every fixture in it
 * to a shop, whatever the branch did. `test/specimen.test.ts` is what holds both.
 */
if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('specimen')) {
  const { Specimen } = await import('./specimen/Specimen.js')
  createRoot(root).render(
    <StrictMode>
      <Specimen />
    </StrictMode>,
  )
} else if (window.location.pathname.startsWith('/portal/')) {
  // `CUS-007`: a customer's own page, reached from a link with no sign-in. It is chosen by the path
  // before `App` is imported into the tree, so nothing of the staff shell (its session read, its
  // write queue, its IndexedDB) runs for somebody who is not staff.
  createRoot(root).render(
    <StrictMode>
      <PortalApp
        pathname={window.location.pathname}
        hash={window.location.hash}
        language={navigator.language}
      />
    </StrictMode>,
  )
} else {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}
